import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ContentChildren,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    ViewChild
} from '@angular/core';
import _ from 'lodash';
import {UIOptionSelectEvent, UISelectOptionComponent} from './ui-select-option/ui-select-option.component';

@Component({
    // tslint:disable-next-line:component-selector
    selector: 'ui-select',
    templateUrl: 'ui-select.component.html'
})
export class UISelectComponent implements AfterViewInit, OnInit, OnDestroy {
    @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;
    /* A ViewChild reference to the action list in the DOM */
    @ViewChild('dropDown') dropdown: ElementRef<HTMLDivElement>;
    @Output() select: EventEmitter<UIOptionSelectEvent> = new EventEmitter<UIOptionSelectEvent>();
    @ContentChildren(UISelectOptionComponent) uiSelectOptionQueryList: QueryList<UISelectOptionComponent>;
    @Input() loading = false;
    @Output() bindValueChange: EventEmitter<unknown> = new EventEmitter<unknown>();
    selectedOption: UISelectOptionComponent;
    /* The index of which the navigation is at */
    public navIndex: number;
    /* Position properties for the action list */
    dropdownProperties: { top: number, left: number, width: string } = {
        top: 0,
        left: 0,
        width: 'auto'
    };
    hostStyling = ['≈flex', '≈flex-nowrap', '≈rounded', 'md:≈rounded-md', '≈p-2', '≈h-12', '≈overflow-ellipses'];
    defaultHostStyling = ['≈border-subdued-borders-default', '≈cursor-pointer'];
    disabledHostStyling = ['≈border-disabled-border', '≈cursor-not-allowed'];
    isLinkedToHost = false;
    /* Callback to reposition the action list for when the page is scrolled */
    handleScroll = _.throttle(() => {
        this.dropdownProperties = {
            top: this.getTopPosition(),
            left: this.getLeftPosition(),
            width: this.getDropdownWidth()
        };
        this.changeDetectorRef.markForCheck();
    }, 100, {leading: true, trailing: true});
    private allUIOptions: UISelectOptionComponent[] = [];
    private allSelectOptions: HTMLElement[] = [];
    private filteredUIOptions: typeof this.allUIOptions = [];
    private filteredSelectOptions: HTMLDivElement[] = [];
    private allGroups: string[] = [];
    private groupedOptions: Element[] = [];

    constructor(
        public host: ElementRef<HTMLElement>,
        private changeDetectorRef: ChangeDetectorRef
    ) {
    }

    private _border: boolean = true;

    @Input() set border(value: boolean) {
        this._border = value;
        this.reapplyHostStyling();
    }

    /* Two-way binding */
    private _bindValue: unknown;

    @Input() set bindValue(value: unknown) {
        this._bindValue = value;
        this.selectBindingValue(value);
    }

    private _searchable = false;
    get searchable() {
        return this._searchable;
    }

    @Input() set searchable(searchable: boolean) {
        this._searchable = searchable;
    }

    private _disabled = false;
    get disabled() {
        return this._disabled;
    }

    @Input() set disabled(disabled: boolean) {
        this._disabled = disabled;
        this.reapplyHostStyling();
    }

    private _searchTerm = '';
    get searchTerm() {
        return this._searchTerm;
    }

    set searchTerm(term: string) {
        this._searchTerm = term;
        this.divideIntoGroups();
    }

    /* Returns weather the trigger element is focused to determine the display of the action list */
    get isHostFocused() {
        this.handleScroll();
        const is = document.activeElement === this.host.nativeElement || document.activeElement === this.searchInput?.nativeElement;
        if (!is && !!this.dropdown?.nativeElement) {
            this.resetNavigation();
        }
        if (is) {
            this.searchInput?.nativeElement.focus();
        }
        return is;
    }

    ngOnInit(): void {

    }

    ngAfterViewInit(): void {
        this.resetAllUIOptions();
        this.resetAllSelectOptions();
        this.resetFilteredUIOptions();
        this.resetFilteredSelectOptions();
        this.resetAllGroups();
        this.resetGroupedOptions();
        this.reapplyHostStyling();
        this.initializeDropdown();
        this.selectBindingValue(this._bindValue);
        this.listenForScrolling();
        this.uiSelectOptionQueryList.changes.subscribe(result => {
            this.initializeDropdown();
            this.resetAllUIOptions();
            this.resetAllSelectOptions();
            this.resetFilteredUIOptions();
            this.resetFilteredSelectOptions();
            this.resetAllGroups();
            this.resetGroupedOptions();
            this.selectBindingValue(this._bindValue);
        });
    }

    initializeDropdown() {
        this.selectDefaultOption();
        this.setOptionOnSelect();
        this.linkToHost();
        this.removeAllButUISelectOptions();
        this.divideIntoGroups();
        this.changeDetectorRef.markForCheck();
    }

    reapplyHostStyling() {
        const hostWidth = Math.ceil(Math.max(...this.allSelectOptions.map(opt => opt.getBoundingClientRect().width)));
        this.host.nativeElement.style.minWidth = `${hostWidth + 20}px`;
        const host = this.host.nativeElement;
        host.classList.add(...this.hostStyling);
        if (this._disabled) {
            host.classList.remove(...this.defaultHostStyling);
            host.classList.add(...this.disabledHostStyling);
        } else {
            host.classList.remove(...this.disabledHostStyling);
            host.classList.add(...this.defaultHostStyling);
        }
        if (this._border) {
            host.classList.add('≈border-2');
        } else {
            host.classList.remove('≈border-2');
        }
    }

    listenForScrolling() {
        this.handleScroll();
        document.addEventListener('scroll', this.handleScroll);
    }

    selectDefaultOption() {
        const uiOptions = this.uiSelectOptionQueryList['_results'] as UISelectOptionComponent[];
        this.selectedOption = uiOptions.find(uiOpt => uiOpt.selected);
        if (!this.selectedOption) {
            return;
        }
        this.select.emit({
            target: undefined,
            value: this.selectedOption.value,
            index: uiOptions.findIndex(uiOpt => uiOpt === this.selectedOption)
        });
    }

    setOptionOnSelect() {
        const uiOptions = this.uiSelectOptionQueryList['_results'] as UISelectOptionComponent[];
        for (const uiOption of uiOptions) {
            uiOption.select = (event) => {
                event.preventDefault();
                this.selectedOption = uiOption;
                this.searchTerm = '';
                for (const option of uiOptions) {
                    if (option === uiOption) {
                        option.selected = true;
                    } else {
                        option.selected = false;
                    }
                }
                (document.activeElement as HTMLElement).blur();
                uiOption.onSelect.emit({
                    target: (event.currentTarget as HTMLElement).parentElement,
                    index: Array.from((event.currentTarget as HTMLElement).parentElement.parentElement.children)
                        .findIndex(el => el === (event.currentTarget as HTMLElement).parentElement),
                    value: uiOption.value
                });
                this.select.emit({
                    target: (event.currentTarget as HTMLElement).parentElement,
                    index: Array.from((event.currentTarget as HTMLElement).parentElement.parentElement.children)
                        .findIndex(el => el === (event.currentTarget as HTMLElement).parentElement),
                    value: uiOption.value
                });
                this.bindValueChange.emit(uiOption.value);
            };
        }
    }

    linkToHost() {
        if (this.isLinkedToHost) {
            return;
        }
        const host = this.host?.nativeElement;
        const handleKeyDown = (event: KeyboardEvent): void => {
            switch (event.key) {
                case 'ArrowUp':
                    event.preventDefault();
                    this.navigateUp();
                    break;
                case 'ArrowDown':
                    event.preventDefault();
                    this.navigateDown();
                    break;
                case 'Enter':
                    event.preventDefault();
                    this.clickNavigatedOption();
                    break;
                default:
                    break;
            }
            this.changeDetectorRef.markForCheck();
        };

        host?.setAttribute('tabindex', '0');
        host?.addEventListener('keydown', handleKeyDown);
        this.isLinkedToHost = true;
    }

    removeAllButUISelectOptions(): void {
        for (const child of Array.from(this.dropdown.nativeElement.children) as HTMLElement[]) {
            if (!child.tagName || child.tagName !== 'UI-SELECT-OPTION') {
                child.remove();
            }
        }
    }

    divideIntoGroups(): void {
        this.dropdown.nativeElement.innerHTML = '';
        this.dropdown.nativeElement.append(...this.groupedOptions);
    }

    getLeftPosition(): number {
        const host = this.host?.nativeElement;
        if (!host || !this.dropdown) {
            return;
        }
        if (host.getBoundingClientRect().left < 0) {
            return 0;
        } else if (host.getBoundingClientRect().right > window.innerWidth - 10) {
            return (window.innerWidth - this.dropdown.nativeElement.getBoundingClientRect().width) - 10;
        }
        const isNearRightEdge = (window.innerWidth - host.getBoundingClientRect().left)
            <= this.dropdown.nativeElement.scrollWidth;
        return isNearRightEdge
            ? host.getBoundingClientRect().right - this.dropdown.nativeElement.scrollWidth
            : host.getBoundingClientRect().left;
    }

    getTopPosition(): number {
        const host = this.host?.nativeElement;
        if (!host || !this.dropdown) {
            return;
        }
        if (host.getBoundingClientRect().top < 0) {
            return 0;
        } else if (host.getBoundingClientRect().bottom > window.innerHeight - 10) {
            return (window.innerHeight - this.dropdown.nativeElement.getBoundingClientRect().height) - 10;
        }
        const isNearBottomEdge = (window.innerHeight - host.getBoundingClientRect().top)
            <= this.dropdown.nativeElement.scrollHeight;
        return isNearBottomEdge
            ? (host.getBoundingClientRect().top - this.dropdown.nativeElement.scrollHeight) - 5
            : host.getBoundingClientRect().bottom + 5;
    }

    getDropdownWidth() {
        const host = this.host?.nativeElement;
        if (!host || !this.dropdown) {
            return;
        }
        return `${host.getBoundingClientRect().width}px`;
    }

    resetNavigation(): void {
        if (!this.allUIOptions) {
            return;
        }
        this.allUIOptions.forEach(option => option.isNavigated = false);
        this.navIndex = undefined;

    }

    highlightNavigatedOption(): void {
        if (!this.allUIOptions) {
            return;
        }
        this.allUIOptions.forEach(option => option.isNavigated = false);
        this.filteredUIOptions[this.navIndex].isNavigated = true;
    }

    navigateDown(): void {
        if (!this.isHostFocused || !this.filteredSelectOptions.length) {
            return;
        }
        if (!this.filteredSelectOptions[this.navIndex + 1]) {
            this.navIndex = 0;
        } else {
            this.navIndex++;
        }
        this.highlightNavigatedOption();
    }

    navigateUp(): void {
        if (!this.isHostFocused || !this.filteredSelectOptions.length) {
            return;
        }
        if (!this.filteredSelectOptions[this.navIndex - 1]) {
            this.navIndex = this.filteredSelectOptions.length - 1;
        } else {
            this.navIndex--;
        }
        this.highlightNavigatedOption();
    }

    clickNavigatedOption(): void {
        const clickEvent = new MouseEvent('click');
        !!this.filteredSelectOptions && this.filteredSelectOptions[this.navIndex].firstChild.dispatchEvent(clickEvent);
    }

    ngOnDestroy(): void {
        document.removeEventListener('scroll', this.handleScroll);
    }

    private resetAllUIOptions() {
        this.allUIOptions = this.uiSelectOptionQueryList?.['_results'] as UISelectOptionComponent[];
    }

    private resetAllSelectOptions() {
        this.allSelectOptions = this.allUIOptions?.map(uiOption => uiOption.host.nativeElement);
    }

    private resetFilteredUIOptions() {
        const regex = new RegExp(
            `${this.searchTerm.trim().replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&')}`,
            'gi'
        );
        this.filteredUIOptions = this.allUIOptions?.filter(uiOpt => regex.test(uiOpt.host.nativeElement.innerText));
    }

    private resetFilteredSelectOptions() {
        this.filteredSelectOptions = this.filteredUIOptions.map(uiOpt => uiOpt.host.nativeElement);
    }

    private resetAllGroups() {
        this.allGroups = [...new Set(this.allSelectOptions.map((option: HTMLElement) => option.dataset.group))];
    }

    private resetGroupedOptions() {
        const groupDivider = (groupName: string) => {
            const divider = document.createElement('div');
            divider.classList.add('≈px-3.5', `${groupName ? '≈py-2' : null}`, 'md:≈px-5.5', `${groupName ? 'md:≈py-4' : null}`,
                '≈border-b', '≈border-divider', '≈font-medium');
            divider.innerText = groupName ?? '';
            return divider;
        };

        let groupedOptions: Element[] = [];
        let optionsWithoutGroup: Element[] = [];

        for (const group of this.allGroups) {
            if (group === undefined) {
                optionsWithoutGroup = this.filteredSelectOptions.filter((option: HTMLElement) =>
                    option.dataset.group === group);
                continue;
            }
            groupedOptions = groupedOptions.concat(
                [groupDivider(group),
                    ...this.filteredSelectOptions.filter((options: HTMLElement) => options.dataset.group === group)]
            );
        }
        if (optionsWithoutGroup.length) {
            groupedOptions = groupedOptions.concat(groupedOptions.length
                ? [groupDivider(''), ...optionsWithoutGroup]
                : optionsWithoutGroup);
        }
        this.groupedOptions = groupedOptions;
    }

    private selectBindingValue(value: unknown) {
        if (!this._bindValue) {
            return;
        }

        const uiOption = this.allUIOptions
            .find(uiOpt => _.isEqual(uiOpt.value, value));
        if (!uiOption) {
            return;
        }

        this.selectedOption = uiOption;
        this.searchTerm = '';
        for (const option of this.allUIOptions) {
            if (option === uiOption) {
                option.selected = true;
            } else {
                option.selected = false;
            }
        }
    }
}
